
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
  Alert,
} from "reactstrap";

import CountUp from "react-countup";

import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import api from "../../../utills/api";

const INITIAL_FORM = {
  channelName: "",
  hotelId: "",
  apiKey: "",
  apiSecret: "",
  endpoint: "",
  enabled: true,
};

const CHANNEL_OPTIONS = [
  {
    value: "BOOKINGCOM",
    label: "Booking.com",
  },
  {
    value: "EXPEDIA",
    label: "Expedia",
  },
  {
    value: "AGODA",
    label: "Agoda",
  },
  {
    value: "AIRBNB",
    label: "Airbnb",
  },
];

const Channels = () => {
  document.title = "HMS | Channels";

  const navigate = useNavigate();

  const [channels, setChannels] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);

  const [isEdit, setIsEdit] = useState(false);

  const [selectedId, setSelectedId] = useState(null);

  const [formData, setFormData] =
    useState(INITIAL_FORM);

  const [loading, setLoading] =
    useState(false);

  const [initialLoading, setInitialLoading] =
    useState(true);

  const [testingId, setTestingId] =
    useState(null);

  const [syncingId, setSyncingId] =
    useState(null);

  const [deletingId, setDeletingId] =
    useState(null);

  const [error, setError] =
    useState("");

  const [successMessage, setSuccessMessage] =
    useState("");

  /* =========================================================
     HELPERS
  ========================================================= */

  const getErrorMessage = useCallback(
    (err, fallback) => {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      );
    },
    []
  );

  const normalizeStatus = useCallback(
    (status) => {
      return String(status || "")
        .trim()
        .toUpperCase();
    },
    []
  );

const isConnected = useCallback(
  (channel) => {
    const status =
      normalizeStatus(
        channel?.status
      );

    return (
      status === "ACTIVE"
    );
  },
  [normalizeStatus]
);

const failed =
  channels.filter(
    (channel) => {
      const status =
        normalizeStatus(
          channel?.status
        );

      return (
        status === "FAILED"
      );
    }
  ).length;

  /* =========================================================
     LOAD CHANNELS
  ========================================================= */

  const loadChannels = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setInitialLoading(true);
        }

        setError("");

        const response =
          await api.get("/channels");

        const data =
          response?.data?.connections;

        setChannels(
          Array.isArray(data)
            ? data
            : []
        );
      } catch (err) {
        console.error(
          "Load Channels Error:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "Unable to load channel connections."
          )
        );

        setChannels([]);
      } finally {
        if (showLoader) {
          setInitialLoading(false);
        }
      }
    },
    [getErrorMessage]
  );

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  /* =========================================================
     FORM
  ========================================================= */

  const resetForm = useCallback(() => {
    setFormData({
      ...INITIAL_FORM,
    });

    setSelectedId(null);
    setIsEdit(false);
  }, []);

  const openCreateModal = () => {
    resetForm();
    setError("");
    setSuccessMessage("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (loading) return;

    setModalOpen(false);
    resetForm();
  };

  const handleInputChange = (e) => {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  /* =========================================================
     VALIDATION
  ========================================================= */

  const validateForm = () => {
    if (!formData.channelName) {
      return "Please select a channel.";
    }

    if (!formData.hotelId.trim()) {
      return "Hotel ID is required.";
    }

    if (!isEdit && !formData.apiKey.trim()) {
      return "API Key is required.";
    }

    if (
      !isEdit &&
      !formData.apiSecret.trim()
    ) {
      return "API Secret is required.";
    }

    if (
      formData.endpoint &&
      !/^https?:\/\//i.test(
        formData.endpoint.trim()
      )
    ) {
      return "Endpoint must start with http:// or https://";
    }

    return null;
  };

  /* =========================================================
     CREATE / UPDATE CHANNEL
  ========================================================= */

  const handleConnectChannel =
    async () => {
      const validationError =
        validateForm();

      if (validationError) {
        setError(validationError);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setSuccessMessage("");

        /*
         * IMPORTANT:
         * endpoint was previously collected in the UI
         * but was not included in the payload.
         */

        const payload = {
          channel:
            formData.channelName.trim(),

          hotelId:
            formData.hotelId.trim(),

          apiKey:
            formData.apiKey.trim(),

          apiSecret:
            formData.apiSecret.trim(),

          endpoint:
            formData.endpoint.trim(),

          enabled:
            Boolean(formData.enabled),
        };

        /*
         * During edit, if the API secret field is intentionally
         * left empty, do not overwrite the existing secret.
         */
        if (
          isEdit &&
          !formData.apiSecret.trim()
        ) {
          delete payload.apiSecret;
        }

        console.log(
          "CHANNEL SAVE PAYLOAD:",
          {
            ...payload,
            apiSecret:
              payload.apiSecret
                ? "***"
                : undefined,
          }
        );

        if (isEdit && selectedId) {
          await api.put(
            `/channels/${selectedId}`,
            payload
          );

          setSuccessMessage(
            "Channel updated successfully."
          );
        } else {
          await api.post(
            "/channels",
            payload
          );

          setSuccessMessage(
            "Channel connected successfully."
          );
        }

        setModalOpen(false);

        resetForm();

        await loadChannels(false);
      } catch (err) {
        console.error(
          "Save Channel Error:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "Failed to save channel connection."
          )
        );
      } finally {
        setLoading(false);
      }
    };

  /* =========================================================
     EDIT
  ========================================================= */

  const handleEdit = (channel) => {
    setSelectedId(channel?._id);
    setIsEdit(true);

    setError("");
    setSuccessMessage("");

    setFormData({
      channelName:
        channel?.channel || "",

      hotelId:
        channel?.hotelId || "",

      /*
       * Some APIs mask credentials.
       * Keep empty so we don't accidentally overwrite them.
       */
      apiKey:
        channel?.apiKey || "",

      apiSecret: "",

      endpoint:
        channel?.endpoint || "",

      enabled:
        channel?.enabled !== false,
    });

    setModalOpen(true);
  };

  /* =========================================================
     DELETE
  ========================================================= */

  const handleDelete =
    async (id) => {
      if (!id) return;

      const channel =
        channels.find(
          (item) =>
            item._id === id
        );

      const channelName =
        channel?.channel ||
        "this channel";

      const confirmed =
        window.confirm(
          `Are you sure you want to delete ${channelName}?`
        );

      if (!confirmed) return;

      try {
        setDeletingId(id);
        setError("");

        await api.delete(
          `/channels/${id}`
        );

        setSuccessMessage(
          "Channel deleted successfully."
        );

        await loadChannels(false);
      } catch (err) {
        console.error(
          "Delete Channel Error:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "Failed to delete channel."
          )
        );
      } finally {
        setDeletingId(null);
      }
    };

  /* =========================================================
     TEST CONNECTION
  ========================================================= */

  const testConnection =
    async (channelId) => {
      if (!channelId) return;

      try {
        setTestingId(channelId);
        setError("");
        setSuccessMessage("");

        const response =
          await api.post(
            `/channels/${channelId}/test`
          );

        setSuccessMessage(
          response?.data?.message ||
            "Connection test completed successfully."
        );

        await loadChannels(false);
      } catch (err) {
        console.error(
          "Channel Test Error:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "Connection test failed."
          )
        );

        await loadChannels(false);
      } finally {
        setTestingId(null);
      }
    };

  /* =========================================================
     SYNC / PUSH ARI
  ========================================================= */

  const syncChannel =
    async (channelId) => {
      if (!channelId) return;

      try {
        setSyncingId(channelId);
        setError("");
        setSuccessMessage("");

        const response =
          await api.post(
            `/channels/${channelId}/sync`
          );

        setSuccessMessage(
          response?.data?.message ||
            "ARI synchronization completed."
        );

        await loadChannels(false);
      } catch (err) {
        console.error(
          "Channel Sync Error:",
          err
        );

        setError(
          getErrorMessage(
            err,
            "ARI synchronization failed."
          )
        );

        await loadChannels(false);
      } finally {
        setSyncingId(null);
      }
    };

  /* =========================================================
     WIDGETS
  ========================================================= */

  const widgets = useMemo(() => {
    const active =
      channels.filter(
        (channel) =>
          channel?.enabled !== false &&
          isConnected(channel)
      ).length;

    const failed =
      channels.filter(
        (channel) => {
          const status =
            normalizeStatus(
              channel?.status
            );

          return (
            status === "FAILED" ||
            status === "DISCONNECTED" ||
            status === "ERROR"
          );
        }
      ).length;

    const configured =
      channels.filter(
        (channel) =>
          Boolean(
            channel?.hotelId
          )
      ).length;

    return [
      {
        title:
          "Connected Channels",

        value:
          channels.length,

        icon:
          "bx bx-link-alt",

        color:
          "#0d6efd",

        bgColor:
          "rgba(13,110,253,.12)",
      },

      {
        title:
          "Active Connections",

        value:
          active,

        icon:
          "bx bx-check-circle",

        color:
          "#198754",

        bgColor:
          "rgba(25,135,84,.12)",
      },

      {
        title:
          "Failed Connections",

        value:
          failed,

        icon:
          "bx bx-error-circle",

        color:
          "#dc3545",

        bgColor:
          "rgba(220,53,69,.12)",
      },

      {
        title:
          "Configured Channels",

        value:
          configured,

        icon:
          "bx bx-cog",

        color:
          "#fd7e14",

        bgColor:
          "rgba(253,126,20,.12)",
      },
    ];
  }, [
    channels,
    isConnected,
    normalizeStatus,
  ]);

  /* =========================================================
     TABLE COLUMNS
  ========================================================= */

  const columns = useMemo(
    () => [
      {
        header:
          "Created At",

        accessorKey:
          "createdAt",

        cell: ({
          getValue,
        }) => {
          const value =
            getValue();

          return value
            ? new Date(
                value
              ).toLocaleString()
            : "-";
        },
      },

      {
        header:
          "Channel",

        accessorKey:
          "channel",

        cell: ({
          getValue,
        }) => {
          const value =
            getValue();

          const channel =
            CHANNEL_OPTIONS.find(
              (item) =>
                item.value ===
                value
            );

          return (
            <strong>
              {channel?.label ||
                value ||
                "-"}
            </strong>
          );
        },
      },

      {
        header:
          "Hotel ID",

        accessorKey:
          "hotelId",

        cell: ({
          getValue,
        }) =>
          getValue() ||
          "-",
      },

      {
        header:
          "Status",

        accessorFn:
          (row) => {
            if (
              row?.enabled ===
              false
            ) {
              return "Disabled";
            }

            return isConnected(
              row
            )
              ? "Connected"
              : normalizeStatus(
                    row?.status
                  ) ||
                  "Pending";
          },

        cell: ({
          getValue,
        }) => {
          const value =
            getValue();

          let color =
            "warning";

          if (
            value ===
            "Connected"
          ) {
            color =
              "success";
          } else if (
            value ===
            "Disabled"
          ) {
            color =
              "secondary";
          } else if (
            value ===
              "FAILED" ||
            value ===
              "Disconnected"
          ) {
            color =
              "danger";
          }

          return (
            <Badge
              color={color}
            >
              {value}
            </Badge>
          );
        },
      },

      {
        header:
          "Sync Status",

        accessorFn:
          (row) => {
            const syncInventory =
              Boolean(
                row?.syncInventory
              );

            const syncRates =
              Boolean(
                row?.syncRates
              );

            const syncBookings =
              Boolean(
                row?.syncBookings
              );

            const syncRestrictions =
              Boolean(
                row?.syncRestrictions
              );

            const enabledSyncs =
              [
                syncInventory,
                syncRates,
                syncBookings,
                syncRestrictions,
              ].filter(
                Boolean
              ).length;

            if (
              enabledSyncs ===
              4
            ) {
              return "Synced";
            }

            if (
              enabledSyncs >
              0
            ) {
              return "Partial";
            }

            return "Critical";
          },

        cell: ({
          getValue,
        }) => {
          const value =
            getValue();

          let color =
            "danger";

          if (
            value ===
            "Synced"
          ) {
            color =
              "success";
          } else if (
            value ===
            "Partial"
          ) {
            color =
              "warning";
          }

          return (
            <Badge
              color={color}
            >
              {value}
            </Badge>
          );
        },
      },

      {
        header:
          "Last Updated",

        accessorKey:
          "updatedAt",

        cell: ({
          getValue,
        }) => {
          const value =
            getValue();

          return value
            ? new Date(
                value
              ).toLocaleString()
            : "-";
        },
      },

      {
        header:
          "Actions",

        cell: ({
          row,
        }) => {
          const channel =
            row.original;

          const id =
            channel?._id;

          const isTesting =
            testingId === id;

          const isSyncing =
            syncingId === id;

          const isDeleting =
            deletingId === id;

          return (
            <div className="d-flex gap-2 flex-wrap">

              <Button
                size="sm"
                color="primary"
                disabled={
                  isTesting ||
                  isSyncing ||
                  isDeleting
                }
                onClick={() =>
                  testConnection(id)
                }
              >
                {isTesting ? (
                  <>
                    <Spinner
                      size="sm"
                      className="me-1"
                    />
                    Testing
                  </>
                ) : (
                  "Test"
                )}
              </Button>

              <Button
                size="sm"
                color="success"
                disabled={
                  isTesting ||
                  isSyncing ||
                  isDeleting ||
                  channel?.enabled ===
                    false
                }
                onClick={() =>
                  syncChannel(id)
                }
              >
                {isSyncing ? (
                  <>
                    <Spinner
                      size="sm"
                      className="me-1"
                    />
                    Syncing
                  </>
                ) : (
                  "Push ARI"
                )}
              </Button>

              <Button
                size="sm"
                color="info"
                disabled={
                  isTesting ||
                  isSyncing ||
                  isDeleting
                }
                onClick={() =>
                  handleEdit(
                    channel
                  )
                }
              >
                Edit
              </Button>

              <Button
                size="sm"
                color="danger"
                disabled={
                  isTesting ||
                  isSyncing ||
                  isDeleting
                }
                onClick={() =>
                  handleDelete(id)
                }
              >
                {isDeleting ? (
                  <>
                    <Spinner
                      size="sm"
                      className="me-1"
                    />
                    Deleting
                  </>
                ) : (
                  "Delete"
                )}
              </Button>

              <Button
                size="sm"
                color="warning"
                onClick={() =>
                  navigate(
                    "/channel-manager/sync-logs"
                  )
                }
              >
                Logs
              </Button>

            </div>
          );
        },
      },
    ],
    [
      deletingId,
      isConnected,
      navigate,
      normalizeStatus,
      syncingId,
      testingId,
    ]
  );

  /* =========================================================
     HEALTH SCORE
  ========================================================= */

  const getHealthScore =
    (channel) => {
      if (
        channel?.enabled ===
        false
      ) {
        return 0;
      }

      if (
        isConnected(channel)
      ) {
        return 100;
      }

      const status =
        normalizeStatus(
          channel?.status
        );

      if (
        status ===
        "FAILED" ||
        status ===
        "ERROR" ||
        status ===
        "DISCONNECTED"
      ) {
        return 25;
      }

      return 50;
    };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="page-content">
      <Container fluid>

        <BreadCrumb
          title="Channel Manager"
          pageTitle="Channels"
        />

        {/* GLOBAL MESSAGES */}

        {error && (
          <Alert
            color="danger"
            toggle={() =>
              setError("")
            }
          >
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            color="success"
            toggle={() =>
              setSuccessMessage("")
            }
          >
            {successMessage}
          </Alert>
        )}

        {/* =====================================================
            WIDGETS
        ===================================================== */}

        <Row>
          {widgets.map(
            (
              item,
              index
            ) => (
              <Col
                xl={3}
                md={6}
                key={index}
              >
                <Card>
                  <CardBody className="d-flex justify-content-between align-items-center">

                    <div>
                      <p className="text-muted text-uppercase fw-semibold mb-1">
                        {item.title}
                      </p>

                      <h3 className="mb-0">
                        <CountUp
                          start={0}
                          end={
                            item.value
                          }
                          duration={1.4}
                        />
                      </h3>
                    </div>

                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{
                        width:
                          "50px",
                        height:
                          "50px",
                        borderRadius:
                          "10px",
                        backgroundColor:
                          item.bgColor,
                      }}
                    >
                      <i
                        className={
                          item.icon
                        }
                        style={{
                          fontSize:
                            "25px",
                          color:
                            item.color,
                        }}
                      />
                    </div>

                  </CardBody>
                </Card>
              </Col>
            )
          )}
        </Row>

        {/* =====================================================
            CHANNEL LIST
        ===================================================== */}

        <Row>
          <Col lg={12}>
            <Card>

              <CardHeader className="d-flex justify-content-between align-items-center">

                <div>
                  <h4 className="card-title mb-1">
                    Connected OTA
                    Channels
                  </h4>

                  <p className="text-muted mb-0">
                    Manage OTA credentials,
                    connectivity and ARI
                    synchronization.
                  </p>
                </div>

                <Button
                  color="success"
                  onClick={
                    openCreateModal
                  }
                  disabled={
                    initialLoading
                  }
                >
                  <i className="bx bx-plus me-1" />
                  Connect New Channel
                </Button>

              </CardHeader>

              <CardBody>

                {initialLoading ? (
                  <div className="text-center py-5">
                    <Spinner
                      color="primary"
                    />

                    <p className="text-muted mt-2 mb-0">
                      Loading channel
                      connections...
                    </p>
                  </div>
                ) : channels.length ===
                  0 ? (
                  <div className="text-center py-5">

                    <div
                      className="d-inline-flex align-items-center justify-content-center mb-3"
                      style={{
                        width:
                          "60px",
                        height:
                          "60px",
                        borderRadius:
                          "50%",
                        backgroundColor:
                          "rgba(13,110,253,.10)",
                      }}
                    >
                      <i
                        className="bx bx-link-alt"
                        style={{
                          fontSize:
                            "30px",
                          color:
                            "#0d6efd",
                        }}
                      />
                    </div>

                    <h5>
                      No OTA channels
                      configured
                    </h5>

                    <p className="text-muted mb-3">
                      Connect your first
                      OTA channel to start
                      synchronizing rates,
                      inventory and
                      restrictions.
                    </p>

                    <Button
                      color="primary"
                      onClick={
                        openCreateModal
                      }
                    >
                      Connect Channel
                    </Button>

                  </div>
                ) : (
                  <TableContainer
                    columns={
                      columns
                    }
                    data={
                      channels
                    }
                    customPageSize={
                      10
                    }
                    isPagination
                    className="text-nowrap"
                    theadClass="table-light"
                  />
                )}

              </CardBody>

            </Card>
          </Col>
        </Row>

        {/* =====================================================
            INFORMATION / HEALTH
        ===================================================== */}

        <Row>

          <Col lg={6}>
            <Card>

              <CardHeader>
                <h4 className="card-title mb-0">
                  Channel Health
                </h4>
              </CardHeader>

              <CardBody>

                {channels.length ===
                0 ? (
                  <p className="text-muted mb-0">
                    No channel connections
                    available.
                  </p>
                ) : (
                  channels.map(
                    (
                      channel
                    ) => {
                      const health =
                        getHealthScore(
                          channel
                        );

                      const connected =
                        isConnected(
                          channel
                        );

                      return (
                        <div
                          className="mb-4"
                          key={
                            channel._id
                          }
                        >

                          <div className="d-flex justify-content-between align-items-center">

                            <div>
                              <strong>
                                {CHANNEL_OPTIONS.find(
                                  (item) =>
                                    item.value ===
                                    channel.channel
                                )?.label ||
                                  channel.channel ||
                                  "Channel"}
                              </strong>

                              <div className="text-muted small mt-1">
                                {channel.hotelId ||
                                  "Hotel ID not configured"}
                              </div>
                            </div>

                            <Badge
                              color={
                                channel.enabled ===
                                false
                                  ? "secondary"
                                  : connected
                                  ? "success"
                                  : "danger"
                              }
                            >
                              {channel.enabled ===
                              false
                                ? "Disabled"
                                : connected
                                ? "Healthy"
                                : "Attention Required"}
                            </Badge>

                          </div>

                          <div className="progress mt-2">
                            <div
                              className={`progress-bar ${
                                channel.enabled ===
                                false
                                  ? "bg-secondary"
                                  : connected
                                  ? "bg-success"
                                  : "bg-danger"
                              }`}
                              role="progressbar"
                              style={{
                                width: `${health}%`,
                              }}
                            >
                              {health}%
                            </div>
                          </div>

                        </div>
                      );
                    }
                  )
                )}

              </CardBody>

            </Card>
          </Col>

          <Col lg={6}>
            <Card>

              <CardHeader>
                <h4 className="card-title mb-0">
                  Channel Information
                </h4>
              </CardHeader>

              <CardBody>

                <div className="d-flex gap-3 mb-3">
                  <i className="bx bx-key text-primary fs-4" />
                  <div>
                    <h6 className="mb-1">
                      OTA Credentials
                    </h6>
                    <p className="text-muted mb-0">
                      Securely configure
                      channel credentials
                      and hotel identifiers.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3 mb-3">
                  <i className="bx bx-wifi text-success fs-4" />
                  <div>
                    <h6 className="mb-1">
                      Connectivity Test
                    </h6>
                    <p className="text-muted mb-0">
                      Test the OTA connection
                      before performing a
                      synchronization.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3 mb-3">
                  <i className="bx bx-transfer text-info fs-4" />
                  <div>
                    <h6 className="mb-1">
                      ARI Synchronization
                    </h6>
                    <p className="text-muted mb-0">
                      Push Availability,
                      Rates and
                      Restrictions to
                      connected channels.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3 mb-3">
                  <i className="bx bx-link text-warning fs-4" />
                  <div>
                    <h6 className="mb-1">
                      Room Mapping
                    </h6>
                    <p className="text-muted mb-0">
                      Map PMS room types
                      and rate plans to
                      OTA room codes.
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-3">
                  <i className="bx bx-history text-danger fs-4" />
                  <div>
                    <h6 className="mb-1">
                      Sync Logs
                    </h6>
                    <p className="text-muted mb-0">
                      Monitor synchronization
                      requests and identify
                      failed operations.
                    </p>
                  </div>
                </div>

              </CardBody>

            </Card>
          </Col>

        </Row>

        {/* =====================================================
            CONNECT / EDIT MODAL
        ===================================================== */}

        <Modal
          isOpen={modalOpen}
          toggle={closeModal}
          backdrop="static"
        >

          <ModalHeader
            toggle={closeModal}
          >
            {isEdit
              ? "Edit Channel"
              : "Connect New Channel"}
          </ModalHeader>

          <ModalBody>

            {error && (
              <Alert
                color="danger"
                className="mb-3"
              >
                {error}
              </Alert>
            )}

            <div className="mb-3">
              <Label>
                Channel{" "}
                <span className="text-danger">
                  *
                </span>
              </Label>

              <Input
                type="select"
                name="channelName"
                value={
                  formData.channelName
                }
                onChange={
                  handleInputChange
                }
                disabled={loading}
              >
                <option value="">
                  Select Channel
                </option>

                {CHANNEL_OPTIONS.map(
                  (
                    channel
                  ) => (
                    <option
                      key={
                        channel.value
                      }
                      value={
                        channel.value
                      }
                    >
                      {
                        channel.label
                      }
                    </option>
                  )
                )}

              </Input>
            </div>

            <div className="mb-3">
              <Label>
                Hotel ID{" "}
                <span className="text-danger">
                  *
                </span>
              </Label>

              <Input
                name="hotelId"
                value={
                  formData.hotelId
                }
                onChange={
                  handleInputChange
                }
                placeholder="Enter OTA Hotel / Property ID"
                disabled={loading}
              />
            </div>

            <div className="mb-3">
              <Label>
                API Key{" "}
                {!isEdit && (
                  <span className="text-danger">
                    *
                  </span>
                )}
              </Label>

              <Input
                name="apiKey"
                value={
                  formData.apiKey
                }
                onChange={
                  handleInputChange
                }
                placeholder={
                  isEdit
                    ? "Leave blank to keep existing key"
                    : "Enter API key"
                }
                disabled={loading}
                autoComplete="off"
              />
            </div>

            <div className="mb-3">
              <Label>
                API Secret{" "}
                {!isEdit && (
                  <span className="text-danger">
                    *
                  </span>
                )}
              </Label>

              <Input
                type="password"
                name="apiSecret"
                value={
                  formData.apiSecret
                }
                onChange={
                  handleInputChange
                }
                placeholder={
                  isEdit
                    ? "Leave blank to keep existing secret"
                    : "Enter API secret"
                }
                disabled={loading}
                autoComplete="new-password"
              />

              {isEdit && (
                <small className="text-muted">
                  Leave blank if you do not
                  want to change the existing
                  API secret.
                </small>
              )}
            </div>

            <div className="mb-3">
              <Label>
                Endpoint URL
              </Label>

              <Input
                name="endpoint"
                value={
                  formData.endpoint
                }
                onChange={
                  handleInputChange
                }
                placeholder="https://api.example.com"
                disabled={loading}
              />

              <small className="text-muted">
                Optional if the channel
                integration uses a predefined
                endpoint.
              </small>
            </div>

            <div className="mb-3">
              <div className="form-check form-switch">

                <Input
                  type="switch"
                  name="enabled"
                  id="channelEnabled"
                  checked={
                    formData.enabled
                  }
                  onChange={
                    handleInputChange
                  }
                  disabled={loading}
                />

                <Label
                  for="channelEnabled"
                  className="form-check-label"
                >
                  Enable Channel
                </Label>

              </div>
            </div>

          </ModalBody>

          <ModalFooter>

            <Button
              color="secondary"
              onClick={
                closeModal
              }
              disabled={
                loading
              }
            >
              Cancel
            </Button>

            <Button
              color="success"
              onClick={
                handleConnectChannel
              }
              disabled={
                loading
              }
            >
              {loading ? (
                <>
                  <Spinner
                    size="sm"
                    className="me-2"
                  />
                  {isEdit
                    ? "Updating..."
                    : "Connecting..."}
                </>
              ) : (
                <>
                  <i className="bx bx-link me-1" />
                  {isEdit
                    ? "Update Channel"
                    : "Save Connection"}
                </>
              )}
            </Button>

          </ModalFooter>

        </Modal>

      </Container>
    </div>
  );
};

export default Channels;